import React, { useEffect, useState } from 'react';
import FileUpload from './FileUpload/FileUpload';
import { TakeoffService } from '../services/TakeoffService';
import { Modal, ModalBody, Badge, Button } from './ui';
import './TakeoffDetailsModal.css';

const TakeoffDetailsModal = ({ takeoff, isOpen, onClose, clients, contacts, onUpdate }) => {
  const [fileAttachments, setFileAttachments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editedData, setEditedData] = useState({});

  // Initialize file attachments and edited data when takeoff changes
  useEffect(() => {
    if (takeoff?.fileAttachments) {
      setFileAttachments(takeoff.fileAttachments);
    } else if (takeoff?.file_attachments) {
      setFileAttachments(takeoff.file_attachments);
    } else {
      setFileAttachments([]);
    }
    setHasChanges(false);
    setEditedData(takeoff || {});
  }, [takeoff]);

  if (!isOpen || !takeoff) return null;

  // Handle file attachment changes
  const handleFilesChange = (newFiles) => {
    setFileAttachments(newFiles);
    setHasChanges(true);
  };

  // Save file attachments
  const handleSaveAttachments = async () => {
    if (!hasChanges || !takeoff) return;

    setIsSaving(true);
    try {
      const updatedTakeoff = await TakeoffService.updateTakeoff(takeoff.id, {
        file_attachments: fileAttachments
      });

      // Call onUpdate callback if provided
      if (onUpdate) {
        onUpdate(updatedTakeoff);
      }

      setHasChanges(false);
      console.log('File attachments saved successfully');
    } catch (error) {
      console.error('Error saving file attachments:', error);
      alert('Failed to save file attachments. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle field changes - auto-save on blur
  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save a single field when it loses focus
  const handleFieldBlur = async (field, providedValue = null) => {
    // Check if the value actually changed
    const originalValue = takeoff[field];
    const newValue = providedValue ?? editedData[field];

    if (originalValue === newValue) {
      return; // No change, don't save
    }

    setIsSaving(true);
    try {
      // Map frontend field names to backend field names
      const fieldMapping = {
        projectName: 'project_name',
        jobNumber: 'job_number',
        status: 'status',
        completionNeededDate: 'completion_needed_date',
        clientId: 'client_id',
        contactId: 'contact_id',
        email: 'email',
        formPhone: 'form_phone',
        formName: 'form_name',
        formClient: 'form_client',
        cadFileAvailable: 'cad_file_available',
        ufredoPrice: 'ufredo_price',
        abCivilPrice: 'ab_civil_price',
        initialAcceptance: 'initial_acceptance'
      };

      const backendField = fieldMapping[field] || field;
      let valueToSave = newValue;

      // Convert values as needed
      if (field === 'clientId' || field === 'contactId') {
        valueToSave = newValue ? parseInt(newValue) : null;
      } else if (field === 'ufredoPrice' || field === 'abCivilPrice') {
        valueToSave = newValue ? parseFloat(newValue) : null;
      }

      const updateData = {
        [backendField]: valueToSave
      };

      const updatedTakeoff = await TakeoffService.updateTakeoff(takeoff.id, updateData);

      if (onUpdate) {
        onUpdate(updatedTakeoff);
      }

      console.log(`Field ${field} updated successfully`);
    } catch (error) {
      console.error(`Error updating field ${field}:`, error);
      alert(`Failed to update ${field}. Please try again.`);
      // Revert to original value on error
      setEditedData(prev => ({
        ...prev,
        [field]: originalValue
      }));
    } finally {
      setIsSaving(false);
    }
  };

  // Parse scopes needed - handle native PostgreSQL arrays and legacy JSON strings
  const parseScopesNeeded = (scopes) => {
    if (!scopes) return null;
    
    // If it's already an array (native PostgreSQL array)
    if (Array.isArray(scopes)) {
      return scopes;
    }
    
    // Legacy support: If it's a JSON string array, parse it
    if (typeof scopes === 'string' && scopes.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(scopes);
        return Array.isArray(parsed) ? parsed : [scopes];
      } catch {
        return [scopes];
      }
    }
    
    // If it's a plain string
    if (typeof scopes === 'string' && scopes.trim()) {
      return [scopes];
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const scopesArray = parseScopesNeeded(takeoff.scopesNeeded);

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    if (!status) return 'neutral';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('done')) return 'success';
    if (statusLower.includes('working') || statusLower.includes('progress')) return 'info';
    if (statusLower.includes('pending') || statusLower.includes('waiting')) return 'warning';
    if (statusLower.includes('cancelled') || statusLower.includes('rejected')) return 'danger';
    return 'neutral';
  };

  // Render field as always editable
  const renderField = (label, field, type = 'text', options = null) => {
    const rawValue = editedData[field] !== undefined ? editedData[field] : takeoff[field];
    // Convert to string for select fields (clientId, contactId) to match option values
    const value = type === 'select' && rawValue != null ? String(rawValue) : rawValue;

    return (
      <div
        style={{
          padding: 'var(--space-4)',
          backgroundColor: 'var(--dark-surface)',
          border: '1px solid var(--gray-700)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </label>
        {type === 'select' ? (
          <select
            value={value || ''}
            onChange={async (e) => {
              const newValue = e.target.value;
              handleFieldChange(field, newValue);
              // Pass newValue directly to avoid state race condition
              handleFieldBlur(field, newValue);
            }}
            disabled={isSaving}
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: 'var(--dark-elevated)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--gray-100)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              cursor: isSaving ? 'not-allowed' : 'pointer'
            }}
          >
            {options && options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : type === 'currency' ? (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                position: 'absolute',
                left: '16px',
                color: 'var(--gray-400)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-medium)',
                pointerEvents: 'none',
                userSelect: 'none'
              }}
            >
              $
            </span>
            <input
              type="number"
              step="0.01"
              value={value || ''}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              onBlur={() => handleFieldBlur(field)}
              disabled={isSaving}
              placeholder="0.00"
              style={{
                width: '100%',
                padding: '12px 16px 12px 28px',
                backgroundColor: 'var(--dark-elevated)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--gray-100)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-medium)',
                cursor: isSaving ? 'not-allowed' : 'text'
              }}
            />
          </div>
        ) : (
          <input
            type={type}
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            onBlur={() => handleFieldBlur(field)}
            disabled={isSaving}
            placeholder={`Enter ${label.toLowerCase()}`}
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: 'var(--dark-elevated)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--gray-100)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              cursor: isSaving ? 'not-allowed' : 'text'
            }}
          />
        )}
      </div>
    );
  };

  return (
    <Modal trackAs="TakeoffDetailsModal" isOpen={isOpen} onClose={onClose} size="xl">
      {/* Custom Header with Icon */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-1)' }}>
              {takeoff.jobNumber && (
                <span
                  style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-400)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                  }}
                >
                  {takeoff.jobNumber}
                </span>
              )}
              {takeoff.status && (
                <Badge variant={getStatusBadgeVariant(takeoff.status)} size="sm">
                  {takeoff.status}
                </Badge>
              )}
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {takeoff.projectName || 'Untitled Project'}
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Takeoff Details
            </p>
          </div>

          {/* Saving indicator */}
          {isSaving && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginLeft: 'auto', color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>
              <span>Saving...</span>
            </div>
          )}
        </div>
      </div>

      <ModalBody>
        {/* Two column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
          {/* LEFT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Project Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Project Information
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {renderField('Project Name', 'projectName', 'text')}
                {renderField('Job Number', 'jobNumber', 'text')}
                {renderField('Status', 'status', 'select', [
                  { value: '', label: 'Select Status' },
                  { value: 'New', label: 'New' },
                  { value: 'Working on it', label: 'Working on it' },
                  { value: 'Completed', label: 'Completed' }
                ])}
                {renderField('Due Date', 'completionNeededDate', 'date')}
              </div>
            </div>

            {/* Client & Contact */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Client & Contact
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Client */}
                {renderField('Client', 'clientId', 'select',
                  [{ value: '', label: 'Select Client' }].concat(
                    clients.map(c => ({ value: c.id.toString(), label: c.name }))
                  )
                )}

                {/* Contact Person */}
                {renderField('Contact Person', 'contactId', 'select',
                  [{ value: '', label: 'Select Contact' }].concat(
                    contacts
                      .filter(c => !editedData.clientId || c.client_id?.toString() === editedData.clientId?.toString())
                      .map(c => ({ value: c.id.toString(), label: c.name }))
                  )
                )}

                {/* Email */}
                {renderField('Email Address', 'email', 'email')}

                {/* Phone */}
                {renderField('Phone Number', 'formPhone', 'tel')}
              </div>
            </div>

            {/* Form Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Form Information
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {renderField('Form Name', 'formName', 'text')}
                {renderField('Form Client', 'formClient', 'text')}
                {renderField('CAD File Available', 'cadFileAvailable', 'select', [
                  { value: '', label: 'Select Option' },
                  { value: 'Yes', label: 'Yes' },
                  { value: 'No', label: 'No' },
                  { value: 'Unknown', label: 'Unknown' }
                ])}
              </div>
            </div>
            </div>

          {/* RIGHT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Technical Requirements */}
            {(takeoff.otherRequest || scopesArray) && (
              <div>
                <h2
                  style={{
                    margin: 0,
                    marginBottom: 'var(--space-4)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)',
                  }}
                >
                  Technical Requirements
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  {takeoff.otherRequest && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Additional Request
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {takeoff.otherRequest}
                      </div>
                    </div>
                  )}

                  {scopesArray && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Scopes Needed
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginTop: 'var(--space-1)' }}>
                        {scopesArray.map((scope, index) => (
                          <Badge key={index} variant="neutral" size="sm">
                            {scope}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* File Attachments Section */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)',
                  }}
                >
                  File Attachments
                </h2>
                {hasChanges && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveAttachments}
                    disabled={isSaving}
                    loading={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
              <div
                style={{
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-lg)',
                }}
              >
                <FileUpload
                  files={fileAttachments}
                  onFilesChange={handleFilesChange}
                  projectId={takeoff.id}
                  clientId={takeoff.clientId}
                  disabled={false}
                  maxFiles={20}
                  type="takeoff"
                />
              </div>
            </div>

            {/* Soil Specifications */}
            {(takeoff.topSoilStrippingDepth || takeoff.topSoilReplacementDepth) && (
              <div>
                <h2
                  style={{
                    margin: 0,
                    marginBottom: 'var(--space-4)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)',
                  }}
                >
                  Soil Specifications
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  {takeoff.topSoilStrippingDepth && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Top Soil Stripping Depth
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {takeoff.topSoilStrippingDepth}
                      </div>
                    </div>
                  )}

                  {takeoff.topSoilReplacementDepth && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Top Soil Replacement Depth
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {takeoff.topSoilReplacementDepth}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pricing Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Pricing
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {renderField('Ufredo Price', 'ufredoPrice', 'currency')}
                {renderField('AB Civil Price', 'abCivilPrice', 'currency')}
                {renderField('Initial Acceptance', 'initialAcceptance', 'text')}
              </div>
            </div>

            {/* Invoice & Payment Information */}
            {(takeoff.invoicedStatus || takeoff.invoicedDate || takeoff.paidDate || takeoff.earthworksTechInvNumber || takeoff.paidToUfredo || takeoff.paidToUfredoDate) && (
              <div>
                <h2
                  style={{
                    margin: 0,
                    marginBottom: 'var(--space-4)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)',
                  }}
                >
                  Invoice & Payment
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  {takeoff.invoicedStatus && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Invoiced Status
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {takeoff.invoicedStatus}
                      </div>
                    </div>
                  )}

                  {takeoff.invoicedDate && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Invoiced Date
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {formatDate(takeoff.invoicedDate)}
                      </div>
                    </div>
                  )}

                  {takeoff.paidDate && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Paid Date
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {formatDate(takeoff.paidDate)}
                      </div>
                    </div>
                  )}

                  {takeoff.earthworksTechInvNumber && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Earthworks Tech Invoice #
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {takeoff.earthworksTechInvNumber}
                      </div>
                    </div>
                  )}

                  {takeoff.paidToUfredo && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Paid to Ufredo
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {takeoff.paidToUfredo}
                      </div>
                    </div>
                  )}

                  {takeoff.paidToUfredoDate && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Paid to Ufredo Date
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {formatDate(takeoff.paidToUfredoDate)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Metadata Information */}
            {(takeoff.createdAt || takeoff.updatedAt || takeoff.airtableClientRecordId || takeoff.airtableCreatedDate) && (
              <div>
                <h2
                  style={{
                    margin: 0,
                    marginBottom: 'var(--space-4)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)',
                  }}
                >
                  Record Information
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  {takeoff.createdAt && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Created Date
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {formatDate(takeoff.createdAt)}
                      </div>
                    </div>
                  )}

                  {takeoff.updatedAt && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Last Updated
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {formatDate(takeoff.updatedAt)}
                      </div>
                    </div>
                  )}

                  {takeoff.airtableClientRecordId && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Airtable Record ID
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {takeoff.airtableClientRecordId}
                      </div>
                    </div>
                  )}

                  {takeoff.airtableCreatedDate && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Airtable Created Date
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {formatDate(takeoff.airtableCreatedDate)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default TakeoffDetailsModal;